import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Phone, Clock, TrendingUp, Heart, CheckCircle2,
  Sparkles, ArrowRight, Menu, X, AlertTriangle, Calendar, Mail,
  Award, Users, MapPin, PlayCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { PLAN_DISPLAY, getPrice } from "@/lib/pricing";

/* ---------- Static content ---------- */

const BUILT_FOR = [
  "Licensed home care agencies",
  "DBHDS-certified group homes",
  "WOSB / SWaM certified operators",
  "Medicaid waiver providers",
  "Owners who've outgrown spreadsheets",
];

const STEPS = [
  {
    icon: Phone,
    title: "Sarah captures every inquiry — 24/7",
    body: "Our embedded AI intake specialist greets every family with empathy, asks the six questions that matter, and never lets a lead sit in a voicemail.",
  },
  {
    icon: Sparkles,
    title: "AI scores and routes the hot ones",
    body: "Every lead is scored 0–100 in real time. Anything 85+ flags as a Hot Lead. 100s trigger an emergency SMS to your phone — even on weekends.",
  },
  {
    icon: TrendingUp,
    title: "You close the right families faster",
    body: "Outreach to discharge planners, CSBs, and support coordinators is generated in 60 seconds. Your dashboard shows exactly what to do next.",
  },
];

// NOTE: replace with verified testimonials before launch.
const TESTIMONIALS = [
  {
    quote: "Within 30 days we booked 11 clients we would have lost. The hot-lead alerts on my phone changed everything — I stopped checking email at 11pm because I knew nothing was slipping through.",
    name: "Denise W.",
    title: "Founder, Brightpath Home Care",
    city: "Baltimore, MD",
  },
  {
    quote: "Two empty beds for six weeks. Two weeks after we started, both were filled with appropriate placements from a CSB I'd never even spoken to. This platform introduced me to my next referral source.",
    name: "Marcus T.",
    title: "Administrator, Capitol Care Group Home",
    city: "Washington, DC",
  },
  {
    quote: "I'm not technical. The onboarding had me live in under an hour and the chat widget started capturing leads that same night. I sleep better now.",
    name: "Yvette R.",
    title: "Owner, Northern Virginia Senior Living Services",
    city: "Manassas, VA",
  },
];

const REGIONS = [
  "Silver Spring", "Montgomery County, MD", "Prince George's County, MD",
  "Baltimore, MD", "Washington, DC", "Northern Virginia",
  "Prince William County, VA", "Fairfax County, VA", "DBHDS Region IV",
];

// Pricing tiers derived from the single source of truth in src/lib/pricing.ts
// so the landing page price can never drift from /pricing.
const PRICING_TIERS = PLAN_DISPLAY.map(t => ({
  name: t.name,
  price: `$${getPrice(t.key, "monthly")}`,
  period: "/mo",
  tagline: t.tagline,
  features: t.features,
  highlighted: t.highlighted,
}));

const FAQS = [
  {
    q: "Is my clients' data really safe?",
    a: "Yes. LegacyGrowth Engine is HIPAA-ready, encrypted in transit and at rest, and a Business Associate Agreement (BAA) is included with every plan — no upgrade required. Built and reviewed by healthcare compliance professionals at H&E Associates LLC.",
  },
  {
    q: "Will this actually work for a small agency?",
    a: "It was built for you first. Most of our customers are 1–25 caregiver agencies and group homes. You don't need a marketing team or an IT department — Sarah does the intake work a four-person front office would do.",
  },
  {
    q: "Is this too complicated for my team?",
    a: "Onboarding takes about 15 minutes. Sarah handles intake, scoring, and routing automatically. Your team just acts on the hot leads we surface. If you can use email, you can use this.",
  },
  {
    q: "Am I locked into a contract?",
    a: "No. Every plan is month-to-month. Cancel anytime from your settings — no calls, no retention scripts, no fees.",
  },
  {
    q: "What if I already use a CRM?",
    a: "LegacyGrowth Engine works alongside your existing tools. Export leads anytime to CSV, sync with email, or use our API. We're the growth engine; we don't ask you to rip and replace.",
  },
];

/* ---------- Page ---------- */

export default function LandingPage() {
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      "LegacyGrowth Engine — Stop losing home care leads. Start growing your agency.";
    const meta =
      document.querySelector('meta[name="description"]') ||
      Object.assign(document.createElement("meta"), { name: "description" });
    meta.setAttribute(
      "content",
      "The 24/7 AI intake and growth platform built for DMV-region home care agencies and DBHDS group homes. HIPAA-ready, BAA included, no contracts.",
    );
    if (!meta.parentNode) document.head.appendChild(meta);
    return () => { document.title = prevTitle; };
  }, []);

  const scrollTo = (id: string) => () => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setNavOpen(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground scroll-smooth">
      {/* ---------- Sticky Nav ---------- */}
      <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Sparkles className="h-4 w-4" />
            </span>
            <span>LegacyGrowth Engine</span>
          </Link>

          <nav className="hidden items-center gap-6 md:flex">
            <button onClick={scrollTo("how")} className="text-sm text-muted-foreground hover:text-foreground">How it works</button>
            <button onClick={scrollTo("pricing")} className="text-sm text-muted-foreground hover:text-foreground">Pricing</button>
            <button onClick={scrollTo("faq")} className="text-sm text-muted-foreground hover:text-foreground">FAQ</button>
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Log in</Link>
            <Button asChild size="sm">
              <Link to="/signup">Start Free Trial</Link>
            </Button>
          </nav>

          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-md md:hidden"
            onClick={() => setNavOpen(v => !v)}
            aria-label="Toggle menu"
          >
            {navOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {navOpen && (
          <div className="border-t border-border bg-background md:hidden">
            <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3">
              <button onClick={scrollTo("how")} className="rounded-md px-2 py-2 text-left text-sm hover:bg-muted">How it works</button>
              <button onClick={scrollTo("pricing")} className="rounded-md px-2 py-2 text-left text-sm hover:bg-muted">Pricing</button>
              <button onClick={scrollTo("faq")} className="rounded-md px-2 py-2 text-left text-sm hover:bg-muted">FAQ</button>
              <Link to="/login" className="rounded-md px-2 py-2 text-sm hover:bg-muted">Log in</Link>
              <Button asChild className="mt-2 w-full">
                <Link to="/signup">Start Free Trial</Link>
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* ---------- 1. Hero ---------- */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/40 via-background to-background" aria-hidden />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center md:py-24">
          <Badge variant="outline" className="mb-6 border-primary/30 bg-primary/5 text-primary">
            <ShieldCheck className="mr-1 h-3 w-3" /> HIPAA-Ready · BAA Included
          </Badge>
          <h1 className="text-3xl font-bold leading-tight tracking-tight md:text-5xl">
            Right now, a family in your service area just contacted three agencies.
            <span className="mt-2 block text-primary">The first one to call back wins the client. Was it you?</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
            LegacyGrowth Engine answers, qualifies, and routes every home care inquiry — 24/7 — so you stop losing revenue to slow follow-up.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/signup">
                Build My Growth Engine — Free for 14 Days <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto" onClick={scrollTo("how")}>
              <PlayCircle className="mr-1 h-4 w-4" /> See How It Works
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            No contracts. Cancel anytime. BAA included.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Built by H&amp;E Associates LLC · MHA · SWaM · WOSB
          </p>
        </div>
      </section>

      {/* ---------- 2. The Mirror ---------- */}
      <section className="mx-auto max-w-5xl px-4 py-12">
        <Card className="border-destructive/20 bg-destructive/5">
          <CardContent className="p-6 md:p-10">
            <div className="grid gap-6 md:grid-cols-[auto_1fr] md:items-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="h-7 w-7" />
              </div>
              <div>
                <p className="text-2xl font-bold leading-tight md:text-3xl">
                  The average home care agency loses ~$47,000/year to uncontacted leads.
                </p>
                <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                  Industry estimate
                </p>
              </div>
            </div>
            <ul className="mt-8 grid gap-3 text-sm text-muted-foreground md:grid-cols-3">
              <li className="rounded-md bg-background/60 p-3">The Saturday call you couldn't pick up.</li>
              <li className="rounded-md bg-background/60 p-3">The voicemail that never got returned.</li>
              <li className="rounded-md bg-background/60 p-3">The family who chose your competitor by Monday.</li>
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* ---------- 3. Exhaustion → Relief ---------- */}
      <section className="mx-auto max-w-3xl px-4 py-16">
        <h2 className="text-2xl font-bold leading-tight md:text-4xl">
          You did not start this business to answer phones at 10pm.
        </h2>
        <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground md:text-lg">
          <p>
            You started this business because you believed families deserved better care.
            You did not sign up to be a full-time lead chaser, an emergency contact for every staffing gap,
            and a compliance officer all at once.
          </p>
          <p>
            But that is what it has become. Every weekend you check your phone hoping no inquiry slipped through.
            Every Monday you find three voicemails you cannot afford to have missed.
          </p>
          <p className="font-medium text-foreground">
            LegacyGrowth Engine does not ask you to work harder. It works while you sleep.
          </p>
        </div>
      </section>

      {/* ---------- 4. Imagine a Monday Morning ---------- */}
      <section className="bg-secondary/40 py-16">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="text-2xl font-bold leading-tight md:text-4xl">Imagine a Monday morning.</h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-foreground md:text-lg">
            <p>
              It's 7am Monday. You open LegacyGrowth Engine and see that Sarah captured three new inquiries
              over the weekend. Two scored above 85 and were automatically flagged as hot leads. One scored 100 —
              an emergency alert went to your phone Saturday night. You called within ten minutes and that family
              is already scheduled for an assessment this afternoon.
            </p>
            <p>
              Your discharge planner at Silver Spring Medical got the outreach email you sent Thursday —
              personalized, professional, and written in 60 seconds. She replied this morning.
            </p>
            <p>
              Your team updated four lead statuses while you were at your kid's soccer game. You did not have to ask.
            </p>
            <p className="font-semibold text-primary">
              This is what your business looks like when it has a growth engine.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- 5. Built For ---------- */}
      <section className="mx-auto max-w-4xl px-4 py-16 text-center">
        <h2 className="text-2xl font-bold leading-tight md:text-4xl">
          You built something real.
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
          Your clients and their families trust you with the most important decisions of their lives.
          You deserve a platform that takes your business as seriously as you do.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {BUILT_FOR.map(item => (
            <Badge key={item} variant="secondary" className="px-3 py-1.5 text-sm">
              {item}
            </Badge>
          ))}
        </div>
      </section>

      {/* ---------- 6. FOMO ---------- */}
      <section className="mx-auto max-w-4xl px-4 pb-16">
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-6 md:p-10">
            <p className="text-lg font-semibold md:text-xl">
              While you read this, your competitors are answering families in your service area.
              Some of them are already using systems like this one.
            </p>
            <p className="mt-3 text-sm text-muted-foreground md:text-base">
              LegacyGrowth Engine gives you the same infrastructure that larger agencies use —
              without the enterprise price tag or the six-month implementation timeline.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* ---------- 7. How It Works ---------- */}
      <section id="how" className="mx-auto max-w-5xl px-4 py-16">
        <div className="text-center">
          <Badge variant="outline" className="mb-3">How it works</Badge>
          <h2 className="text-2xl font-bold md:text-4xl">From inquiry to assessment in three steps.</h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Card key={s.title} className="h-full">
              <CardContent className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="text-lg font-semibold leading-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
                {/* Product video / screenshot slot — drop a 60s screen recording here later */}
                <div className="mt-4 flex h-28 items-center justify-center rounded-md border border-dashed border-border bg-muted/40 text-xs text-muted-foreground">
                  Product preview
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ---------- 8. Trust bar ---------- */}
      <section className="border-y border-border bg-secondary/30 py-10">
        <div className="mx-auto grid max-w-5xl gap-6 px-4 sm:grid-cols-2 md:grid-cols-4">
          {[
            { icon: ShieldCheck, label: "HIPAA-Ready", sub: "BAA included with every plan" },
            { icon: Award, label: "Healthcare Compliance", sub: "Built by H&E Associates LLC" },
            { icon: Users, label: "MHA · SWaM · WOSB", sub: "Credentialed leadership" },
            { icon: CheckCircle2, label: "No contracts", sub: "Cancel anytime, no fees" },
          ].map(item => (
            <div key={item.label} className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <item.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- 9. Testimonials ---------- */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="text-center">
          <h2 className="text-2xl font-bold md:text-4xl">Trusted by agencies in the DMV.</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Real owners. Real results. Real names.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map(t => (
            <Card key={t.name} className="h-full">
              <CardContent className="flex h-full flex-col p-6">
                <Heart className="h-5 w-5 text-primary" />
                <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground">"{t.quote}"</p>
                <div className="mt-6 border-t border-border pt-4">
                  <p className="text-sm font-semibold">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.title}</p>
                  <p className="text-xs text-muted-foreground">{t.city}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ---------- 10. DMV Community ---------- */}
      <section className="bg-secondary/30 py-16">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <Badge variant="outline" className="mb-3">
            <MapPin className="mr-1 h-3 w-3" /> Designed for the DMV
          </Badge>
          <h2 className="text-2xl font-bold md:text-4xl">
            Built for the home care community we live in.
          </h2>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {REGIONS.map(r => (
              <Badge key={r} variant="secondary" className="px-3 py-1.5 text-sm">{r}</Badge>
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-base text-muted-foreground md:text-lg">
            LegacyGrowth Engine is the operational backbone behind The $1B Agency — a community of
            home care owners and group home operators building scalable businesses together.
          </p>
        </div>
      </section>

      {/* ---------- 11. Pricing ---------- */}
      <section id="pricing" className="mx-auto max-w-6xl px-4 py-16">
        <div className="text-center">
          <h2 className="text-2xl font-bold md:text-4xl">Simple pricing. Built to pay for itself.</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            14-day free trial. No credit card. Cancel anytime.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {PRICING_TIERS.map(tier => (
            <Card
              key={tier.name}
              className={
                "transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-primary/60 " +
                (tier.highlighted ? "relative border-primary shadow-lg" : "h-full")
              }
            >
              {tier.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">Most popular</Badge>
                </div>
              )}
              <CardContent className="flex h-full flex-col p-6">
                <h3 className="text-lg font-semibold">{tier.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{tier.price}</span>
                  <span className="text-sm text-muted-foreground">{tier.period}</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{tier.tagline}</p>
                <ul className="mt-6 flex-1 space-y-2">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 space-y-2">
                  <Button asChild className="w-full" variant={tier.highlighted ? "default" : "outline"}>
                    <Link to="/signup">Start Free Trial</Link>
                  </Button>
                  <Link
                    to="/pricing"
                    className="block text-center text-xs text-muted-foreground hover:text-foreground"
                  >
                    See full pricing
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ---------- 12. FAQ ---------- */}
      <section id="faq" className="mx-auto max-w-3xl px-4 py-16">
        <div className="text-center">
          <h2 className="text-2xl font-bold md:text-4xl">Questions you probably have.</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            (And a few you haven't asked out loud.)
          </p>
        </div>
        <Accordion type="single" collapsible className="mt-8">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`}>
              <AccordionTrigger className="text-left text-base">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ---------- 13. Final CTA ---------- */}
      <section className="bg-gradient-to-b from-secondary/30 to-primary/10 py-16">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <div className="mb-8 flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> HIPAA-Ready</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Live in under 60 minutes</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> No contracts</span>
          </div>
          <h2 className="text-3xl font-bold leading-tight md:text-5xl">
            Stop losing leads this weekend.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
            Set up LegacyGrowth Engine today and Sarah will be answering inquiries by tonight.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/signup">
                Build My Growth Engine <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            {/* <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <a href="mailto:hello@legacygrowthengine.com">
                <Mail className="mr-1 h-4 w-4" /> Talk to the team
              </a>
            </Button> */}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Free 14-day trial · BAA included · No contracts
          </p>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="border-t border-border bg-background py-10">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2 font-semibold">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
                <Sparkles className="h-3.5 w-3.5" />
              </span>
              LegacyGrowth Engine
            </div>
            <p className="mt-2 max-w-xs text-xs text-muted-foreground">
              The growth engine for DMV-region home care agencies and DBHDS group homes. A product of H&amp;E Associates LLC.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6 text-sm sm:grid-cols-3">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Product</p>
              <ul className="space-y-1.5">
                <li><Link to="/pricing" className="text-muted-foreground hover:text-foreground">Pricing</Link></li>
                <li><Link to="/affiliate" className="text-muted-foreground hover:text-foreground">Affiliate</Link></li>
                <li><Link to="/api-docs" className="text-muted-foreground hover:text-foreground">API Docs</Link></li>
              </ul>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Legal</p>
              <ul className="space-y-1.5">
                <li><Link to="/privacy" className="text-muted-foreground hover:text-foreground">Privacy</Link></li>
                <li><Link to="/terms" className="text-muted-foreground hover:text-foreground">Terms</Link></li>
                <li><Link to="/hipaa" className="text-muted-foreground hover:text-foreground">HIPAA Notice</Link></li>
              </ul>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Account</p>
              <ul className="space-y-1.5">
                <li><Link to="/login" className="text-muted-foreground hover:text-foreground">Log in</Link></li>
                <li><Link to="/signup" className="text-muted-foreground hover:text-foreground">Start free trial</Link></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mx-auto mt-8 max-w-6xl px-4 text-xs text-muted-foreground">
          © {new Date().getFullYear()} H&amp;E Associates LLC. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
